import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  Outlet,
} from "react-router-dom";

import { ConfigProvider, theme, App as AntdApp } from "antd";
import { isTutorAreaRole, normalizeRoleName, useAuth } from "./contexts/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { CohortWorkspaceProvider } from "./features/cohort-workspace/CohortWorkspaceContext";

import { Layout } from "./components/admin/layout/Layout";
import { TutorLayout } from "./components/tutor/layout/Layout";
import { TutorProgramProvider } from "./contexts/TutorProgramContext";
import QuizLightArea from "./components/quiz/QuizLightArea";
import { AttendanceTheme } from "./pages/admin/attendance/ui";

const lazyNamed = <T extends Record<string, unknown>, K extends keyof T>(
  loader: () => Promise<T>,
  exportName: K,
) => lazy(() => loader().then((module) => ({ default: module[exportName] as React.ComponentType })));

const AdminDashboard = lazyNamed(() => import("./pages/admin/Dashboard"), "AdminDashboard");
const TransactionsPage = lazyNamed(() => import("./pages/admin/transactions"), "TransactionsPage");
const FinancialStatisticsPage = lazy(() => import("./pages/admin/financial-statistics"));
const StandupPage = lazy(() => import("./pages/admin/Standup"));
const TaskManager = lazy(() => import("./pages/admin/TaskManager"));
const FinancePage = lazy(() => import("./pages/admin/Finance"));
const FeesPage = lazy(() => import("./pages/admin/Fees"));
const AuditLogPage = lazy(() => import("./pages/admin/audit"));
const ReferralManagementPage = lazy(() => import("./pages/admin/ReferralManagement"));
const InterviewsPage = lazy(() => import("./pages/admin/Interviews"));
const ReviewManagement = lazy(() => import("./pages/admin/Review"));
const ApplicationPage = lazy(() => import("./pages/admin/Applications"));
const MarketingDash = lazy(() => import("./pages/admin/MarketingDash"));
const StudentsPage = lazy(() => import("./pages/admin/Students"));
const DigitalIds = lazy(() => import("./pages/admin/DigitalIds"));
const StudentStatistics = lazy(() => import("./pages/admin/StudentStats"));
const DeferredStudentsPage = lazy(() => import("./pages/admin/Deferred"));
const ApplicationStats = lazy(() => import("./pages/admin/ApplicationStats"));
const EventManager = lazy(() => import("./pages/admin/EventManager"));
const TemplatesPage = lazy(() => import("./pages/admin/Templates"));
const ProgramLibraryPage = lazy(() => import("./pages/admin/ProgramLibraryPage"));
const NotificationsPage = lazy(() => import("./pages/admin/Notifications"));
const NotificationSettings = lazy(() => import("./components/notifications/NotificationSettings"));
const SenderPage = lazy(() => import("./pages/admin/Sender"));
const SequencesPage = lazy(() => import("./pages/admin/Sequences"));
const AiSettingsPage = lazy(() => import("./pages/admin/AiSettings"));
const ContactManagement = lazy(() => import("./pages/admin/campaign/ContactManagement"));
const CampaignList = lazy(() => import("./pages/admin/campaign/CampaignList"));
const CreateCampaign = lazy(() => import("./pages/admin/campaign/CreateCampaign"));
const UserManagement = lazy(() => import("./pages/admin/Users"));
const Programs = lazy(() => import("./pages/admin/Programs"));
const ProgramDetails = lazy(() => import("./pages/admin/ProgramDetails"));
const Cohorts = lazy(() => import("./pages/admin/Cohorts"));
const Intakes = lazy(() => import("./pages/admin/Intakes"));
const StudentLedger = lazy(() => import("./pages/admin/StudentLedger"));
const StudentLedgerDetail = lazy(() => import("./pages/admin/StudentLedgerDetail"));
const CoursesPage = lazy(() => import("./pages/admin/Courses"));
const CourseDetail = lazy(() => import("./pages/admin/CourseDetail"));
const Grading = lazy(() => import("./pages/admin/Grading"));
const QuizDashboard = lazy(() => import("./pages/admin/QuizDashboard"));
const QuizBuilderPage = lazy(() => import("./pages/admin/QuizBuilder"));
const TaskBuilderPage = lazy(() => import("./pages/admin/TaskBuilder"));
const TutorQuizBuilderPage = lazy(() => import("./pages/admin/QuizBuilder").then((m) => ({ default: m.TutorQuizBuilderPage })));
const QuizAttempt = lazyNamed(() => import("./components/quiz/QuizAttempt"), "QuizAttempt");
const QuizResult = lazyNamed(() => import("./components/quiz/QuizResult"), "QuizResult");
const RedlistedStudents = lazy(() => import("./pages/admin/RedlistedStudents"));
const TicketManagement = lazy(() => import("./pages/admin/TicketManagement"));
const CurriculumManager = lazy(() => import("./pages/admin/Curriculum"));
const PracticalsLibrary = lazy(() => import("./pages/admin/PracticalsLibrary"));
const LibrarySuggestions = lazy(() => import("./pages/admin/LibrarySuggestions"));
const ClassDeliveries = lazy(() => import("./pages/admin/ClassDeliveries"));
const GearItems = lazy(() => import("./pages/admin/gear/GearItems"));
const GearBundles = lazy(() => import("./pages/admin/gear/GearBundles"));
const GearBookings = lazy(() => import("./pages/admin/gear/GearBookings"));
const GearSignOffs = lazy(() => import("./pages/admin/gear/GearSignOffs"));
const DebtCollection = lazy(() => import("./pages/admin/debt-collection"));
const LoanManager = lazy(() => import("./pages/admin/LoanManager"));
const InterviewQueue = lazy(() => import("./pages/admin/InterviewQueue"));
const InterviewSupport = lazy(() => import("./pages/admin/InterviewSupport"));
const AdmissionsPage = lazy(() => import("./pages/admin/Admissions"));
const LeadManagerPage = lazy(() => import("./pages/admin/LeadManager"));
const ProspectiveStudentsPage = lazy(() => import("./pages/admin/ProspectiveStudents"));
const WhatsAppInbox = lazy(() => import("./pages/admin/WhatsAppInbox"));
const ProjectManager = lazy(() => import("./pages/admin/ProjectManager"));
const EmailInbox = lazy(() => import("./pages/admin/EmailInbox"));
const Internships = lazy(() => import("./pages/admin/Internships"));
const AdminLoginPage = lazy(() => import("./pages/public/Team"));
const InterviewDisplay = lazy(() => import("./pages/public/InterviewDisplay"));
const UnauthorizedPage = lazy(() => import("./pages/public/Unauthorized"));
const AssistantPage = lazy(() => import("./features/assistant/pages/AssistantPage"));
const AssistantActivityPage = lazy(() => import("./features/assistant/pages/ActivityPage"));
const AttendanceSessionsPage = lazyNamed(() => import("./pages/admin/attendance/SessionsPage"), "AttendanceSessionsPage");
const AttendanceStudentsPage = lazyNamed(() => import("./pages/admin/attendance/StudentsPage"), "AttendanceStudentsPage");
const AttendanceRulesPage = lazyNamed(() => import("./pages/admin/attendance/RulesPage"), "AttendanceRulesPage");
const TakeAttendancePage = lazyNamed(() => import("./pages/admin/attendance/TakeAttendancePage"), "TakeAttendancePage");
const TutorDashboard = lazy(() => import("./pages/tutor/Dashboard"));
const TutorTaskManagement = lazy(() => import("./pages/tutor/TaskManagement"));
const TutorAttendanceManagement = lazy(() => import("./pages/tutor/Attendance"));
const Certificate = lazy(() => import("./pages/admin/Certificate"));
const GraduationReservations = lazy(() => import("./pages/admin/GraduationReservations"));
const TutorCurriculum = lazy(() => import("./pages/tutor/TutorCurriculum"));
const TutorGrading = lazy(() => import("./pages/tutor/Grading"));
const TutorQuizDashboard = lazy(() => import("./pages/tutor/QuizDashboard"));
const TutorAttendanceSessions = lazy(() => import("./pages/tutor/AttendanceSessions"));
const TutorTicketManagement = lazy(() => import("./pages/tutor/TutorTicketManagement"));
const AdminPresentationsPage = lazy(() => import("./features/presentations/pages/AdminPresentationsPage"));
const TutorPresentationsPage = lazy(() => import("./features/presentations/pages/TutorPresentationsPage"));
const AdminPresentationEditorPage = lazyNamed(
  () => import("./features/presentations/pages/PresentationEditorPage"),
  "AdminPresentationEditorPage",
);
const TutorPresentationEditorPage = lazyNamed(
  () => import("./features/presentations/pages/PresentationEditorPage"),
  "TutorPresentationEditorPage",
);
const PresentationLaunchPage = lazy(() => import("./features/presentations/pages/PresentationLaunchPage"));

const { defaultAlgorithm } = theme;

const RouteLoadingFallback = () => (
  <div
    role="status"
    aria-label="Loading page"
    style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}
  >
    Loading...
  </div>
);

const getRoleName = (role: any): string => normalizeRoleName(role);
const isTutorRole = (role: string) => isTutorAreaRole(role);
const isStudentRole = (role: string) => role === "student";

// Protected Route component
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  area?: "admin" | "tutor";
}> = ({ children, area }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/team" replace />;
  }

  const role = getRoleName(user?.role);

  if (area === "admin" && (isTutorRole(role) || isStudentRole(role))) {
    return <Navigate to={isTutorRole(role) ? "/tutor/dashboard" : "/unauthorized"} replace />;
  }

  if (area === "tutor" && isStudentRole(role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return area ? (
    <CohortWorkspaceProvider>{children}</CohortWorkspaceProvider>
  ) : (
    <>{children}</>
  );
};

// Public Route - redirect if already authenticated
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>;
  }

  if (isAuthenticated) {
    const role = getRoleName(user?.role);
    return <Navigate to={isTutorRole(role) ? "/tutor/dashboard" : "/admin/dashboard"} replace />;
  }

  return <>{children}</>;
};

const AdminRoutes = () => (
  <Layout>
    <Routes>
      <Route path="dashboard" element={<AdminDashboard />} />
      <Route path="applications" element={<ApplicationPage />} />
      <Route path="Admissions" element={<AdmissionsPage />} />
      <Route path="LeadManager" element={<LeadManagerPage />} />
      <Route path="EmailInbox" element={<EmailInbox />} />
      <Route path="WhatsAppInbox" element={<WhatsAppInbox />} />
      <Route path="ApplicationStats" element={<ApplicationStats />} />
      <Route path="interviews" element={<InterviewsPage />} />
      <Route path="review" element={<ReviewManagement />} />
      <Route path="interview-queue" element={<InterviewQueue />} />
      <Route path="interview-support" element={<InterviewSupport />} />
      <Route path="Students" element={<StudentsPage />} />
      <Route path="digital-ids" element={<DigitalIds />} />
      <Route path="prospective-students" element={<ProspectiveStudentsPage />} />
      <Route path="internships" element={<Internships />} />
      <Route path="certificates" element={<Certificate />} />
      <Route path="RedlistedStudents" element={<RedlistedStudents />} />
      <Route path="StudentStats" element={<StudentStatistics />} />
      <Route path="MarketingDash" element={<MarketingDash />} />
      <Route path="Deferred" element={<DeferredStudentsPage />} />
      <Route path="finance" element={<FinancePage />} />
      <Route path="fees" element={<FeesPage />} />
      <Route path="transactions" element={<TransactionsPage />} />
      <Route path="audit" element={<AuditLogPage />} />
      <Route path="financial-statistics" element={<FinancialStatisticsPage />} />
      <Route path="debt-collection" element={<DebtCollection />} />
      <Route path="loan-manager" element={<LoanManager />} />
      <Route path="ReferralManagement" element={<ReferralManagementPage />} />
      <Route path="standup" element={<StandupPage />} />
      <Route path="TaskManager" element={<TaskManager />} />
      <Route path="Schedules" element={<ProgramLibraryPage />} />
      <Route path="events" element={<EventManager />} />
      <Route path="graduation-reservations" element={<GraduationReservations />} />
      <Route path="graduation-reservations/check-in/:passCode" element={<GraduationReservations />} />
      <Route path="projects" element={<ProjectManager />} />
      <Route path="TicketManagement" element={<TicketManagement />} />
      <Route path="Templates" element={<TemplatesPage />} />
      <Route path="notifications" element={<NotificationsPage />} />
      <Route path="NotificationSettings" element={<NotificationSettings />} />
      <Route path="ai-settings" element={<AiSettingsPage />} />
      <Route path="Sender" element={<SenderPage />} />
      <Route path="Sequences" element={<SequencesPage />} />
      <Route path="campaign/contacts" element={<ContactManagement />} />
      <Route path="campaign/campaigns" element={<CampaignList />} />
      <Route path="campaign/create" element={<CreateCampaign />} />
      <Route path="Users" element={<UserManagement />} />
      <Route path="Programs" element={<Programs />} />
      <Route path="cohorts" element={<Cohorts />} />
      <Route path="cohorts/intakes" element={<Intakes />} />
      <Route path="student-ledger" element={<StudentLedger />} />
      <Route path="student-ledger/:id" element={<StudentLedgerDetail />} />
      <Route path="programs/:programId" element={<ProgramDetails />} /> {/* Program Details with Streams & Teams */}
      <Route path="courses" element={<CoursesPage />} /> {/* New Course Manager */}
      <Route path="courses/:id" element={<CourseDetail />} />
      <Route path="grading" element={<Grading />} /> {/* Grading Center */}
      <Route path="QuizDashboard" element={<QuizDashboard />} />
      <Route path="quizzes/:id" element={<QuizAttempt />} />
      <Route path="quiz-result/:id" element={<QuizResult />} />
      <Route path="Curriculum" element={<CurriculumManager />} />
      <Route path="practicals-library" element={<PracticalsLibrary />} />
      <Route path="library-suggestions" element={<LibrarySuggestions />} />
      <Route path="class-deliveries" element={<ClassDeliveries />} />
      <Route path="gear" element={<Navigate to="/admin/gear/items" replace />} />
      <Route path="gear/items" element={<GearItems />} />
      <Route path="gear/bundles" element={<GearBundles />} />
      <Route path="gear/bookings" element={<GearBookings />} />
      <Route path="gear/sign-offs" element={<GearSignOffs />} />
      <Route path="assistant" element={<AssistantPage />} />
      <Route path="assistant/activity" element={<AssistantActivityPage />} />
      <Route path="assistant/:conversationId" element={<AssistantPage />} />
      <Route path="DashboardAgentPage" element={<Navigate to="/admin/assistant" replace />} />
      <Route path="agent" element={<Navigate to="/admin/assistant" replace />} />
      <Route path="presentations" element={<AdminPresentationsPage />} />
      <Route path="presentations/new" element={<AdminPresentationsPage />} />
      <Route path="presentations/:presentationId/edit" element={<AdminPresentationEditorPage />} />

      {/* Attendance: Sessions, Students and Rules tabs; Take attendance opens from a session. */}
      <Route path="attendance/sessions" element={<AttendanceTheme><AttendanceSessionsPage /></AttendanceTheme>} />
      <Route path="attendance/students" element={<AttendanceTheme><AttendanceStudentsPage /></AttendanceTheme>} />
      <Route path="attendance/rules" element={<AttendanceTheme><AttendanceRulesPage /></AttendanceTheme>} />
      <Route path="attendance/session/:sessionId" element={<AttendanceTheme><TakeAttendancePage /></AttendanceTheme>} />
      {/* Retired attendance pages: old bookmarks land on the tab that replaced them. */}
      <Route path="attendance" element={<Navigate to="/admin/attendance/sessions" replace />} />
      <Route path="attendance/dashboard" element={<Navigate to="/admin/attendance/sessions" replace />} />
      <Route path="attendance/manual" element={<Navigate to="/admin/attendance/sessions" replace />} />
      <Route path="attendance/policies" element={<Navigate to="/admin/attendance/rules" replace />} />
      <Route path="AttendanceManagement" element={<Navigate to="/admin/attendance/rules" replace />} />
      <Route path="sessions" element={<Navigate to="/admin/attendance/sessions" replace />} />

      <Route index element={<Navigate to="/admin/dashboard" replace />} />
    </Routes>
  </Layout>
);

const TutorRoutes = () => (
  <TutorLayout>
    <Routes>
      <Route path="dashboard" element={<TutorDashboard />} />
      <Route path="tasks" element={<TutorTaskManagement />} />
      <Route path="curriculum" element={<TutorCurriculum />} />
      <Route path="attendance" element={<TutorAttendanceManagement />} />
      <Route path="attendance/sessions" element={<TutorAttendanceSessions />} />
      <Route path="grading" element={<TutorGrading />} />
      <Route path="quizzes" element={<TutorQuizDashboard />} />
      <Route path="tickets" element={<TutorTicketManagement />} />
      <Route path="presentations" element={<TutorPresentationsPage />} />
      <Route path="presentations/new" element={<TutorPresentationsPage />} />
      <Route path="presentations/:presentationId/edit" element={<TutorPresentationEditorPage />} />
      <Route index element={<Navigate to="/tutor/dashboard" replace />} />
    </Routes>
  </TutorLayout>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/interview-display" element={<InterviewDisplay />} />
      <Route path="/present/:presentationId" element={
        <ProtectedRoute>
          <PresentationLaunchPage />
        </ProtectedRoute>
      } />

      <Route path="/team" element={
        <PublicRoute>
          <AdminLoginPage />
        </PublicRoute>
      } />

      {/* Full-screen quiz builders, outside the dashboard layouts */}
      <Route path="/admin/quiz-builder/:quizId" element={
        <ProtectedRoute area="admin">
          <QuizLightArea><QuizBuilderPage mode="quiz" area="admin" /></QuizLightArea>
        </ProtectedRoute>
      } />
      <Route path="/admin/quiz-template-builder/:templateId" element={
        <ProtectedRoute area="admin">
          <QuizLightArea><QuizBuilderPage mode="template" area="admin" /></QuizLightArea>
        </ProtectedRoute>
      } />
      <Route path="/tutor/quiz-builder/:quizId" element={
        <ProtectedRoute area="tutor">
          <TutorProgramProvider>
            <QuizLightArea><TutorQuizBuilderPage /></QuizLightArea>
          </TutorProgramProvider>
        </ProtectedRoute>
      } />

      <Route path="/admin/task-builder/:taskId" element={
        <ProtectedRoute area="admin">
          <QuizLightArea><TaskBuilderPage mode="task" area="admin" /></QuizLightArea>
        </ProtectedRoute>
      } />
      <Route path="/admin/task-template-builder/:templateId" element={
        <ProtectedRoute area="admin">
          <QuizLightArea><TaskBuilderPage mode="template" area="admin" /></QuizLightArea>
        </ProtectedRoute>
      } />
      <Route path="/tutor/task-builder/:taskId" element={
        <ProtectedRoute area="tutor">
          <QuizLightArea><TaskBuilderPage mode="task" area="tutor" /></QuizLightArea>
        </ProtectedRoute>
      } />

      {/* Protected Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute area="admin">
          <AdminRoutes />
        </ProtectedRoute>
      } />

      {/* Protected Tutor Routes */}
      <Route path="/tutor/*" element={
        <ProtectedRoute area="tutor">
          <TutorRoutes />
        </ProtectedRoute>
      } />

      {/* Other Routes */}
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/" element={<Navigate to="/team" replace />} />
      <Route path="*" element={<Navigate to="/team" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ConfigProvider
          theme={{
            algorithm: defaultAlgorithm,
            token: {
              colorPrimary: "#ffd700",
              colorInfo: "#ffd700",
            },
          }}
        >
          <AntdApp>
            <Suspense fallback={<RouteLoadingFallback />}>
              <AppRoutes />
            </Suspense>
          </AntdApp>
        </ConfigProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
